import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  CalendarClock,
  Mail,
  Clock,
  Loader2,
  Send,
  Check,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { submitContact, BOOKING_URL, AGENCY_EMAIL } from "@/lib/api";
import { Container, Eyebrow, Reveal, MaskedLines, GlowOrb, goldBtn } from "@/components/kit";
import { cn } from "@/lib/utils";

const schema = z.object({
  name: z.string().min(2, "Please enter your full name"),
  work_email: z.string().email("Enter a valid work email"),
  company: z.string().min(2, "Please enter your company name"),
  role: z.string().min(1, "Select your role"),
  website: z.string().optional().or(z.literal("")),
  monthly_meeting_target: z.string().min(1, "Choose a target"),
  message: z.string().min(10, "A little more detail helps (10+ characters)"),
});

const roles = [
  "Founder / CEO",
  "Revenue / CRO",
  "Sales Leader",
  "Marketing Leader",
  "Growth / Demand Gen",
  "Other",
];
const targets = ["5–10 / month", "10–25 / month", "25–50 / month", "50+ / month"];

const prepChecklist = [
  "Your ICP or best-fit customer description",
  "Current pipeline & outbound setup (if any)",
  "Average deal size and sales cycle",
  "Your monthly qualified-meeting goal",
];

const inputCls =
  "h-12 bg-navy-900 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-gold focus-visible:border-gold/40";

const Field = ({ label, error, children, testid }) => (
  <div>
    <Label className="text-sm text-white/70">{label}</Label>
    <div className="mt-2">{children}</div>
    {error && (
      <p data-testid={`error-${testid}`} className="mt-1.5 text-xs text-red-400">
        {error.message}
      </p>
    )}
  </div>
);

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      work_email: "",
      company: "",
      role: "",
      website: "",
      monthly_meeting_target: "",
      message: "",
    },
  });

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      await submitContact(values);
      toast.success("Message received", {
        description: "Thanks — we'll reply within 1 business day.",
      });
      reset();
      setDone(true);
      setTimeout(() => setDone(false), 2500);
    } catch (e) {
      toast.error("Something went wrong", {
        description: "Please try again or email us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-14 md:pt-44 md:pb-16">
        <GlowOrb className="left-1/2 top-0 h-[460px] w-[680px] -translate-x-1/2 opacity-80" />
        <div className="absolute inset-0 -z-10 grain opacity-[0.035]" />
        <Container>
          <Eyebrow>Let's Talk Pipeline</Eyebrow>
          <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[0.98] text-white">
            <MaskedLines lines={["Tell us who you", "want to meet —"]} delay={0.1} />
            <MaskedLines
              lines={["we'll book them"]}
              lineClassName="text-gold text-glow-gold"
              delay={0.1 + 2 * 0.11}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-white/65"
          >
            Share a few details and we'll come back with a tailored plan — or grab a discovery call
            slot directly. No pushy sales, just pipeline strategy.
          </motion.p>
        </Container>
      </section>

      {/* Two-column suite */}
      <section className="pb-24 md:pb-28">
        <Container className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
          {/* Left: form */}
          <Reveal className="rounded-3xl border border-white/10 bg-navy-800 p-7 md:p-10">
            <h2 className="font-display text-2xl font-semibold text-white">Send a message</h2>
            <p className="mt-2 text-sm text-white/55">
              All fields marked are required. We reply within one business day.
            </p>

            <form
              onSubmit={handleSubmit(onSubmit)}
              data-testid="contact-form"
              className="mt-8 grid gap-5"
              noValidate
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name" error={errors.name} testid="name">
                  <Input
                    data-testid="contact-name"
                    className={inputCls}
                    placeholder="Jordan Rivera"
                    {...register("name")}
                  />
                </Field>
                <Field label="Work email" error={errors.work_email} testid="work_email">
                  <Input
                    data-testid="contact-email"
                    type="email"
                    className={inputCls}
                    placeholder="jordan@company.com"
                    {...register("work_email")}
                  />
                </Field>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Company" error={errors.company} testid="company">
                  <Input
                    data-testid="contact-company"
                    className={inputCls}
                    placeholder="Company Inc."
                    {...register("company")}
                  />
                </Field>
                <Field label="Role" error={errors.role} testid="role">
                  <Controller
                    control={control}
                    name="role"
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger data-testid="contact-role" className={cn(inputCls, "data-[placeholder]:text-white/30")}>
                          <SelectValue placeholder="Select your role" />
                        </SelectTrigger>
                        <SelectContent className="bg-navy-800 border-white/10 text-white">
                          {roles.map((r) => (
                            <SelectItem key={r} value={r} className="focus:bg-gold/15 focus:text-gold">
                              {r}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </Field>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Website (optional)" error={errors.website} testid="website">
                  <Input
                    data-testid="contact-website"
                    className={inputCls}
                    placeholder="https://company.com"
                    {...register("website")}
                  />
                </Field>
                <Field
                  label="Monthly meeting target"
                  error={errors.monthly_meeting_target}
                  testid="monthly_meeting_target"
                >
                  <Controller
                    control={control}
                    name="monthly_meeting_target"
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger data-testid="contact-target" className={cn(inputCls, "data-[placeholder]:text-white/30")}>
                          <SelectValue placeholder="Choose a target" />
                        </SelectTrigger>
                        <SelectContent className="bg-navy-800 border-white/10 text-white">
                          {targets.map((t) => (
                            <SelectItem key={t} value={t} className="focus:bg-gold/15 focus:text-gold">
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </Field>
              </div>

              <Field label="What are you trying to achieve?" error={errors.message} testid="message">
                <Textarea
                  data-testid="contact-message"
                  rows={5}
                  className={cn(inputCls, "h-auto py-3 resize-none")}
                  placeholder="Tell us about your ICP, offer, and pipeline goals…"
                  {...register("message")}
                />
              </Field>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className={cn(goldBtn, "mt-2 w-full disabled:opacity-70 disabled:hover:translate-y-0")}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : done ? (
                  <>
                    <Check className="h-4 w-4" /> Sent
                  </>
                ) : (
                  <>
                    Send Message <Send className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          </Reveal>

          {/* Right: booking + prep */}
          <div className="space-y-6">
            <Reveal className="relative overflow-hidden rounded-3xl border border-gold/25 bg-navy-800 p-8 shadow-[0_0_60px_rgba(255,215,0,0.08)]">
              <div className="pointer-events-none absolute -right-14 -top-14 h-48 w-48 gold-radial" />
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold text-navy-900">
                <CalendarClock className="h-6 w-6" />
              </span>
              <h3 className="mt-6 font-display text-2xl font-semibold text-white">
                Book a discovery call
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">
                Prefer to talk it through? Grab a 30-minute slot and we'll map your ICP and model
                your pipeline live.
              </p>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-booking-cta"
                className={cn(goldBtn, "mt-6 w-full")}
              >
                Book A Discovery Call
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>

              <div className="mt-7 space-y-3 border-t border-white/10 pt-6">
                <div className="flex items-center gap-3 text-sm text-white/70">
                  <Clock className="h-4 w-4 text-gold" />
                  Response SLA: within 1 business day
                </div>
                <a
                  href={`mailto:${AGENCY_EMAIL}`}
                  data-testid="contact-email-link"
                  className="flex items-center gap-3 text-sm text-white/70 hover:text-gold transition-colors"
                >
                  <Mail className="h-4 w-4 text-gold" />
                  {AGENCY_EMAIL}
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="rounded-3xl border border-white/10 bg-navy-800 p-8">
              <p className="font-mono-jb text-xs uppercase tracking-[0.24em] text-white/45">
                Come prepared with
              </p>
              <ul className="mt-5 space-y-3">
                {prepChecklist.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-white/75">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Closing statement */}
      <section data-testid="contact-closing" className="relative overflow-hidden border-t border-white/10 py-20 md:py-24">
        <GlowOrb className="left-1/2 top-1/2 h-[420px] w-[520px] -translate-x-1/2 -translate-y-1/2 opacity-80" />
        <Container>
          <Reveal className="text-center">
            <h2 className="mx-auto max-w-3xl font-display text-3xl md:text-5xl font-medium tracking-tight text-white">
              Stop paying for activity.
              <br />
              <span className="text-gold text-glow-gold">Start paying for outcomes.</span>
            </h2>
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
